/**
 * DashboardPage — the composable canvas replacing PortfolioDashboardPage/
 * AccountDashboardPage (M2 T7). Mounted at BOTH '/' and '/account/:id' in
 * App.tsx. Account-scoping resolution mirrors the old pages' combined
 * behavior exactly: `/account/:id`'s route param takes precedence, else the
 * persisted useSelectedAccount store (the mechanism '/' always used) —
 * there is no third, prop-based entry point anymore, since nothing embeds
 * this page the way PortfolioDashboardPage used to embed AccountDashboardPage
 * (that delegation is now this same internal resolution).
 */
import { useEffect, useState, lazy, Suspense } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router';
import type { Layout } from 'react-grid-layout';
import { useSelectedAccount } from '@/store/selectedAccountStore';
import { useAccountDashboard } from '@/api/dashboard';
import { useAuthStore } from '@/store/authStore';
import { useDashboards, useDashboard, useUpdateWidgetLayout, useDeleteWidget } from '@/api/dashboardConfig';
import { DashboardSwitcher } from '@/components/dashboard/DashboardSwitcher';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { AddWidgetPalette } from '@/components/dashboard/AddWidgetPalette';
import { RenameWidgetModal } from '@/components/dashboard/RenameWidgetModal';
import { MoveResizeModal } from '@/components/dashboard/MoveResizeModal';
import { RagBadge } from '@/components/RagBadge';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { DeleteAccountDialog } from '@/components/DeleteAccountDialog';
import { TrashIcon } from '@/components/icons';
import { btnGhostStyle, btnPrimaryStyle } from '@/components/ProjectForm';
import type { DashboardContext, DashboardWidgetResponse } from '@/types/domain';

const MetricWidgetEditorLazy = lazy(() =>
  import('@/components/dashboard/metric/MetricWidgetEditor').then((m) => ({ default: m.MetricWidgetEditor })),
);

interface DashboardSummary {
  id: string;
  context: DashboardContext;
  accountId: string | null;
  isDefault: boolean;
}

const pageHeadingStyle = {
  fontFamily: 'var(--font-sans)', fontSize: 27, fontWeight: 600, color: 'var(--text-hi)',
  letterSpacing: '-0.01em', margin: 0,
} as const;

/** Prefer an accountId-matching override over the accountId===null template. */
function resolveDefaultDashboardId(
  dashboards: DashboardSummary[],
  context: DashboardContext,
  accountId: string | null,
): string | null {
  const inContext = dashboards.filter((d) => d.context === context && d.isDefault);
  if (context === 'ACCOUNT' && accountId) {
    const override = inContext.find((d) => d.accountId === accountId);
    if (override) return override.id;
  }
  const template = inContext.find((d) => d.accountId === null);
  return template?.id ?? inContext[0]?.id ?? null;
}

export function DashboardPage() {
  const params = useParams<{ id: string }>();
  const storeAccountId = useSelectedAccount((s) => s.accountId);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const effectiveAccountId = params.id ?? storeAccountId ?? null;
  const context: DashboardContext = effectiveAccountId ? 'ACCOUNT' : 'GLOBAL';

  const { data: dashboards = [], isLoading: isListLoading, isError: isListError } = useDashboards();
  const requestedId = searchParams.get('dashboardId');
  // Discard a ?dashboardId= override once the list is loaded and its dashboard
  // is known to belong to another context (e.g. a GLOBAL selection lingering in
  // the URL after an account is picked). An unknown id (list still loading, or a
  // freshly-created dashboard not yet in the list) is kept as-is.
  const requestedDash = requestedId ? dashboards.find((d) => d.id === requestedId) : undefined;
  const requestedIsValid = requestedId != null && (!requestedDash || requestedDash.context === context);
  const resolvedId = (requestedIsValid ? requestedId : null)
    ?? resolveDefaultDashboardId(dashboards, context, effectiveAccountId);
  const { data: dashboard, isLoading: isDashboardLoading, isError: isDashboardError } = useDashboard(resolvedId ?? '');

  const showLoading = isListLoading || isDashboardLoading;
  const showError = isListError || isDashboardError;

  const accountHeader = useAccountDashboard(effectiveAccountId ?? '');
  const canDeleteAccount = useAuthStore((s) => s.hasPermission('ACCOUNT_MANAGE'));
  const [showDelete, setShowDelete] = useState(false);

  // Edit mode — pencil toggle, staged layout (drag/resize only; add/rename/
  // remove commit immediately via their own modals). See plan T8 Design.
  const [editMode, setEditMode] = useState(false);
  const [stagedLayout, setStagedLayout] = useState<Layout | null>(null);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const [showAddPalette, setShowAddPalette] = useState(false);
  const [showMetricEditor, setShowMetricEditor] = useState(false);
  const [renameTarget, setRenameTarget] = useState<DashboardWidgetResponse | null>(null);
  const [moveResizeTarget, setMoveResizeTarget] = useState<DashboardWidgetResponse | null>(null);
  const [configureTarget, setConfigureTarget] = useState<DashboardWidgetResponse | null>(null);
  // Bumped on Discard to force DashboardGrid to remount — the /legacy RGL
  // wrapper only re-derives its internal layout state when the `layouts`
  // PROP deep-equal-changes, and Discard doesn't touch dashboard.widgets, so
  // without a remount the canvas keeps rendering the discarded drag/resize
  // positions even though the persisted layout is unchanged.
  const [discardCount, setDiscardCount] = useState(0);
  const updateLayout = useUpdateWidgetLayout();

  // Switching dashboards (or leaving/re-entering with a different id) must
  // never carry edit state across — otherwise a staged drag on dashboard A
  // can commit against dashboard B's widget ids, or the user can be stranded
  // in edit mode on a dashboard they can't edit (canEdit gates the exit
  // affordance below).
  const dashboardId = dashboard?.id;
  useEffect(() => {
    setEditMode(false);
    setStagedLayout(null);
    setShowUnsavedPrompt(false);
  }, [dashboardId]);

  // Edit-mode permission gate (design brief: "owner or DASHBOARD_MANAGE for
  // shared" — spec line 73). A PERSONAL dashboard is editable only by its
  // owner; a SHARED one only by a member holding DASHBOARD_MANAGE. Both
  // seeded landing dashboards are SHARED, so without this gate every member
  // would see editing chrome that the M1 backend then 403s on the first
  // write. memberId comes from authStore (T2), never JWT-decoded.
  const memberId = useAuthStore((s) => s.memberId);
  const canManageShared = useAuthStore((s) => s.hasPermission('DASHBOARD_MANAGE'));
  const canEdit = dashboard
    ? dashboard.visibility === 'PERSONAL'
      ? dashboard.ownerMemberId === memberId
      : canManageShared
    : false;

  function selectDashboard(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('dashboardId', id);
      return next;
    });
  }

  /** The active dashboard was just deleted: drop the dead ?dashboardId so the
   * page falls back to the context default instead of an error card. */
  function clearDashboardSelection() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('dashboardId');
      return next;
    });
  }

  function togglePencil() {
    // Guards the double-submit window: while the batch PUT is in flight,
    // stagedLayout isn't cleared until onSuccess, so without this guard a
    // second Done click here would re-open the unsaved prompt mid-save.
    if (updateLayout.isPending) return;
    if (editMode && stagedLayout) {
      setShowUnsavedPrompt(true);
      return;
    }
    setShowUnsavedPrompt(false);
    setEditMode((v) => !v);
  }

  function handleDone() {
    if (updateLayout.isPending) return;
    if (!stagedLayout || !dashboard) {
      setEditMode(false);
      return;
    }
    updateLayout.mutate(
      { dashboardId: dashboard.id, items: stagedLayout.map((l) => ({ id: l.i, x: l.x, y: l.y, w: l.w, h: l.h })) },
      // onSuccess only: a failed batch PUT (network/403/validation) must
      // leave the staged layout intact and edit mode open — the global
      // MutationCache toast already surfaces the error, and there is no
      // retry path if we discard the user's work on failure too.
      { onSuccess: () => { setStagedLayout(null); setEditMode(false); } },
    );
  }

  function handleDiscard() {
    setStagedLayout(null);
    setEditMode(false);
    setShowUnsavedPrompt(false);
    setDiscardCount((c) => c + 1);
  }

  const deleteWidget = useDeleteWidget();
  function deleteWidgetImmediately(widget: DashboardWidgetResponse) {
    deleteWidget.mutate({ id: widget.id, dashboardId: widget.dashboardId });
    // A staged drag/resize batch predates this removal — same desync class
    // move-resize/add already guard against (clearStagedLayout below): the
    // dashboard detail refetch re-derives the grid from the new widget list,
    // so a filtered-but-stale stagedLayout would either snap remaining
    // widgets back to persisted positions under the user (phantom "unsaved
    // changes") or, on a single-widget dashboard, leave stagedLayout=[] —
    // still truthy — triggering a phantom prompt and a Save that PUTs
    // items:[]. Clear it outright, consistent with add/move-resize.
    setStagedLayout(null);
  }

  // Move/resize and add both change grid geometry outside the staged-layout
  // flow (they commit immediately via their own endpoints) — any staged
  // drag/resize snapshot predates their effect, and once the dashboard
  // detail refetches, RGL re-derives from the new positions while a stale
  // stagedLayout would silently overwrite them again on the next Save.
  // Rename and Configure are grid-neutral (title/config only), so neither
  // needs this.
  function clearStagedLayout() {
    setStagedLayout(null);
  }

  return (
    <div>
      {effectiveAccountId && accountHeader.data && (
        <div className="reveal-1" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Breadcrumbs items={[{ label: 'Accounts', to: '/accounts' }, { label: accountHeader.data.accountName }]} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={pageHeadingStyle}>
                {accountHeader.data.accountName}
              </h1>
              <RagBadge rag={accountHeader.data.worstHealth} />
              {canDeleteAccount && (
                <button
                  type="button"
                  onClick={() => setShowDelete(true)}
                  className="pressable"
                  style={{
                    marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                    height: 36, boxSizing: 'border-box', padding: '0 16px', border: 'none',
                    borderRadius: 'var(--radius-pill)',
                    background: 'color-mix(in srgb, var(--sem-red) 12%, transparent)',
                    color: 'var(--sem-red)',
                    fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 600, cursor: 'pointer',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'color-mix(in srgb, var(--sem-red) 20%, transparent)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'color-mix(in srgb, var(--sem-red) 12%, transparent)'; }}
                >
                  <TrashIcon /> Delete account
                </button>
              )}
            </div>
            {showDelete && (
              <DeleteAccountDialog
                account={{ id: accountHeader.data.accountId, name: accountHeader.data.accountName, key: accountHeader.data.accountKey }}
                onClose={() => setShowDelete(false)}
                onDeleted={() => { setShowDelete(false); navigate('/accounts'); }}
              />
            )}
          </div>
        </div>
      )}

      {effectiveAccountId && !accountHeader.data && (
        <div className="reveal-1" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Breadcrumbs items={[{ label: 'Accounts', to: '/accounts' }, { label: 'Account' }]} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={pageHeadingStyle}>Account</h1>
              {accountHeader.isError && (
                <button
                  type="button"
                  onClick={() => accountHeader.refetch()}
                  className="pressable focus-ring"
                  style={{
                    display: 'inline-flex', alignItems: 'center', height: 32, boxSizing: 'border-box',
                    padding: '0 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
                    background: 'var(--surface)', color: 'var(--text)', fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-sm)', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Retry
                </button>
              )}
            </div>
            {accountHeader.isError && (
              <p role="alert" style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--text-low)', margin: 0 }}>
                Couldn&apos;t load the account details.
              </p>
            )}
          </div>
        </div>
      )}

      {!effectiveAccountId && (
        <div className="reveal-1" style={{ marginBottom: 16 }}>
          <h1 style={{ ...pageHeadingStyle, margin: '0 0 6px' }}>
            {dashboard?.name ?? 'Dashboard'}
          </h1>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--text-low)', margin: 0 }}>
            Delivery health, throughput, and schedule across every active engagement.
          </p>
        </div>
      )}

      <div className="reveal-2" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <DashboardSwitcher
          dashboards={dashboards}
          activeDashboardId={dashboard?.id ?? resolvedId}
          context={context}
          accountId={effectiveAccountId}
          onSelect={selectDashboard}
          onActiveDeleted={clearDashboardSelection}
        />
        {canEdit && editMode && (
          <button type="button" onClick={() => setShowAddPalette(true)} className="pressable focus-ring" style={{ ...btnGhostStyle, height: 32, fontSize: 'var(--text-sm)' }}>
            + Add widget
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={togglePencil}
            aria-label={editMode ? 'Done editing' : 'Edit layout for this dashboard'}
            className="pressable focus-ring"
            style={{
              marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, boxSizing: 'border-box',
              padding: '0 14px', border: 'none', borderRadius: 'var(--radius-pill)',
              background: editMode ? 'var(--brand)' : 'var(--search-pill-bg)',
              color: editMode ? 'var(--text-inverted)' : 'var(--text)',
              fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {editMode ? 'Done' : 'Edit layout'}
          </button>
        )}
      </div>

      {editMode && (
        <div
          role="status"
          className="reveal-2"
          style={{
            marginBottom: 14, padding: '8px 14px', borderRadius: 'var(--radius-panel)',
            background: 'var(--brand-soft)', color: 'var(--text)', fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)', fontWeight: 500,
          }}
        >
          Editing layout — drag, resize, add or remove widgets
        </div>
      )}

      {editMode && showUnsavedPrompt && (
        <div
          role="alert"
          aria-label="Unsaved layout changes"
          className="pop-in"
          style={{
            marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--radius-panel)',
            background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}
        >
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
            You have unsaved changes to this layout.
          </span>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button type="button" onClick={handleDiscard} disabled={updateLayout.isPending} className="pressable focus-ring" style={btnGhostStyle}>Discard</button>
            <button type="button" onClick={() => { setShowUnsavedPrompt(false); handleDone(); }} disabled={updateLayout.isPending} className="pressable focus-ring" style={btnPrimaryStyle}>
              Save
            </button>
          </div>
        </div>
      )}

      {showLoading && (
        <div className="skeleton" style={{ height: 400, borderRadius: 'var(--radius-md)' }} aria-label="Loading dashboard" />
      )}

      {!showLoading && showError && (
        <div
          role="alert"
          style={{
            background: 'var(--error-bg)', border: '1px solid var(--error-border)', borderRadius: 'var(--radius-md)',
            padding: '16px 18px', color: 'var(--error)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)',
          }}
        >
          Failed to load this dashboard.
        </div>
      )}

      {!showLoading && !showError && dashboard?.widgets && (
        <DashboardGrid
          key={`${dashboard.id}-${discardCount}`}
          widgets={dashboard.widgets}
          accountId={effectiveAccountId}
          editMode={editMode && canEdit && !updateLayout.isPending}
          onLayoutChange={setStagedLayout}
          onRename={setRenameTarget}
          onMoveResize={setMoveResizeTarget}
          onConfigure={setConfigureTarget}
          onRemove={(widget) => {
            // WidgetOverflowMenu.handleRemove already gates on window.confirm
            // before it ever calls this (EditableWidgetCard → DashboardGrid →
            // here) — confirming a second time here would double-prompt.
            // Delete is immediate (design brief: "remove already committed
            // immediately"); the DELETE hook lives with the modals below —
            // invoked inline here via a tiny local mutation trigger.
            deleteWidgetImmediately(widget);
          }}
        />
      )}

      {showAddPalette && dashboard && (
        <AddWidgetPalette
          dashboardId={dashboard.id}
          existingWidgets={dashboard.widgets ?? []}
          onClose={() => setShowAddPalette(false)}
          onAdded={() => { setShowAddPalette(false); clearStagedLayout(); }}
          onAddMetric={() => { setShowAddPalette(false); setShowMetricEditor(true); }}
        />
      )}
      {showMetricEditor && dashboard && (
        <Suspense fallback={null}>
          <MetricWidgetEditorLazy
            dashboardId={dashboard.id}
            existingWidgets={dashboard.widgets ?? []}
            onClose={() => setShowMetricEditor(false)}
            onAdded={() => { setShowMetricEditor(false); clearStagedLayout(); }}
          />
        </Suspense>
      )}
      {renameTarget && (
        <RenameWidgetModal widget={renameTarget} onClose={() => setRenameTarget(null)} onRenamed={() => setRenameTarget(null)} />
      )}
      {moveResizeTarget && (
        <MoveResizeModal widget={moveResizeTarget} onClose={() => setMoveResizeTarget(null)} onSaved={() => { setMoveResizeTarget(null); clearStagedLayout(); }} />
      )}
      {configureTarget && dashboard && (
        <Suspense fallback={null}>
          <MetricWidgetEditorLazy
            mode="edit"
            widget={configureTarget}
            dashboardId={dashboard.id}
            existingWidgets={dashboard.widgets ?? []}
            onClose={() => setConfigureTarget(null)}
            onAdded={() => setConfigureTarget(null)}
          />
        </Suspense>
      )}
    </div>
  );
}
